import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import PropTypes from 'prop-types';
import { Grid, Button, Icon, Table, Header } from 'semantic-ui-react';
import i18next from 'i18next';
import CloseOrderModal from './CloseOrderModal';
import CreditOrderModal from './CreditOrderModal';
import CancelOrderModal from './CancelOrderModal';
import { closeOrder, cancelOrder, creditPayment, resendOrder, confirm, generateReceipt, fetchOrder } from '../../../actions';
import { getCurrentRestaurant } from '../../../selectors';
import { formatPrice } from '../../../helpers/format';
import { buildOrderObject } from '../../../helpers/order';

class OrderDetails extends Component {
    state = {
        print: false,
        orderNotFound: false,
        showFinishConfirmation: false,
        showCancelationForm: false,
        showCreditForm: false,
        order: null
    }

    constructor(props) {
        super(props);

        this.handleCompleteOrder = this.handleCompleteOrder.bind(this);
        this.handleCancelOrder = this.handleCancelOrder.bind(this);
        this.handleCreditOrder = this.handleCreditOrder.bind(this);
    }

    componentDidMount() {
        const onSuccess = order => this.setState({ order: buildOrderObject(order) });
        const onFailure = () => this.setState({ orderNotFound: true });

        this.props.fetchOrder(this.props.match.params.restId, this.props.match.params.orderId, onSuccess, onFailure);
    }

    handlePrint() {
        this.setState({ print: true }, () => {
            window.print();
            this.setState({ print: false });
        });
    }

    handleCompleteOrder(order) {
        this.props.closeOrder(this.props.restaurant.id, order.guid);
    }

    handleCancelOrder(order, form) {
        const { reason, details, notifyUser } = form;
        this.props.cancelOrder(this.props.restaurant.id, order.guid, reason, details, notifyUser);
        this.setState({ showCancelationForm: false });
    }

    handleCreditOrder(creditForm) {
        this.props.creditPayment(this.props.restaurant.id, creditForm);
        this.setState({ showCreditForm: false });
    }

    handleResendToPOS(restId, orderId) {
        this.props.confirm({
            title: i18next.t('resendTitle'),
            message: i18next.t('resendMessage'),
            onConfirm: () => this.props.resendOrder(restId, orderId)
        });
    }

    handleGenerateReceipt(restId, orderId, replacementVoucher) {
        this.props.generateReceipt(restId, orderId, i18next.language, replacementVoucher, (data) => {
            document.getElementById('downloadBtn').setAttribute('href', 'data:application/pdf;charset=utf-8;base64,' + data);
            document.getElementById('downloadBtn').setAttribute('download', `Order: ${this.state.order.id}`);
            document.getElementById('downloadBtn').click();
        });
    }

    renderAddress(delivery) {
        const addressParts = [delivery.fullAddress];

        if (delivery.entrance) {
            addressParts.push(`${i18next.t('entrance')} ${delivery.entrance}`);
        }
        if (delivery.floor) {
            addressParts.push(`${i18next.t('floor')} ${delivery.floor}`);
        }
        if (delivery.apartment) {
            addressParts.push(`${i18next.t('apartment')} ${delivery.apartment}`);
        }

        let address = addressParts.join(', ');

        return address;
    }

    renderItemDetails(item) {
        return (
            <div>
                {!item.toppings.length ? null :
                    <div>
                        {item.toppings.map((topping, index) =>
                            <div key={index}>
                                {topping.name + (topping.price ? ` (${formatPrice(topping.price, this.props.restaurant.currencyCode)}+)` : '')}
                                &rlm;
                            </div>
                        )}
                    </div>
                }
            </div>
        );
    }

    render() {
        const { order, orderNotFound } = this.state;
        const { restId, orderId } = this.props.match.params;

        if (orderNotFound) {
            return <Redirect to={`/restaurant/${restId}/orders`} />;
        }

        if (!order) {
            return null;
        }

        let containerStyle = { padding: 10 };
        if (this.state.print) {
            containerStyle = {
                background: 'white',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                padding: 10,
                zIndex: 1000,
            };
        }

        const transactions = order.payments.reduce((transactions, p) => transactions.concat(p.transactions || []), []);

        return (
            <div>
                <Grid stackable columns={this.state.print ? 1 : 2} style={containerStyle}>
                    <Grid.Column>
                        <Table padded>
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell>{i18next.t('order')}</Table.Cell>
                                    <Table.Cell>{`#${order.id}`}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>{i18next.t('orderType')}</Table.Cell>
                                    <Table.Cell>{i18next.t(order.cartType || 'payment')}</Table.Cell>
                                </Table.Row>
                                {!order.delivery ? null :
                                    <Table.Row>
                                        <Table.Cell>{i18next.t('deliveryAddress')}</Table.Cell>
                                        <Table.Cell>
                                            {this.renderAddress(order.delivery)}
                                            {order.delivery.remarks &&
                                                <div>{`${i18next.t('comments')}: ${order.delivery.remarks}`}</div>
                                            }
                                        </Table.Cell>
                                    </Table.Row>
                                }
                                <Table.Row>
                                    <Table.Cell>{i18next.t('receiveTime')}</Table.Cell>
                                    <Table.Cell>{new Date(order.createdAt).toLocaleString()}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>{i18next.t('customer')}</Table.Cell>
                                    <Table.Cell>
                                        {[...new Set(order.payments.map(({ customer }) => [customer.name || customer.alias, customer.phone].filter(c => !!c).join(', ')))].map((user, i) => <div key={i}>{user}</div>)}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>{i18next.t('comments')}</Table.Cell>
                                    <Table.Cell>{order.remarks}</Table.Cell>
                                </Table.Row>
                                {!order.delivery ? null :
                                    <Table.Row>
                                        <Table.Cell>{i18next.t('deliveryFee')}</Table.Cell>
                                        <Table.Cell>{formatPrice(order.delivery.fee, this.props.restaurant.currencyCode, 2)}</Table.Cell>
                                    </Table.Row>
                                }
                                {!order.totalDiscount ? null :
                                    <Table.Row>
                                        <Table.Cell>{i18next.t('discount')}</Table.Cell>
                                        <Table.Cell>{formatPrice(order.totalDiscount, this.props.restaurant.currencyCode, 2)}</Table.Cell>
                                    </Table.Row>
                                }
                                {!order.totalTip ? null :
                                    <Table.Row>
                                        <Table.Cell>{i18next.t('tip')}</Table.Cell>
                                        <Table.Cell>{formatPrice(order.totalTip, this.props.restaurant.currencyCode, 2)}</Table.Cell>
                                    </Table.Row>
                                }
                                <Table.Row>
                                    <Table.Cell>{i18next.t('payment')}</Table.Cell>
                                    <Table.Cell style={{ display: 'flex' }}>
                                        <b>{formatPrice(order.totalPayment, this.props.restaurant.currencyCode, 2)}</b>
                                        {!transactions || !transactions.length ? null :
                                            <Fragment>
                                                <div style={{ width: 5 }} />
                                                {'('}
                                                {transactions.map((tran, index) => (
                                                    <span key={index}>
                                                        {index > 0 && ", "}
                                                        <span title={tran.amount} style={{ cursor: 'pointer' }}>{`${tran.paymentMethod} `}</span>
                                                    </span>
                                                ))}
                                                {')'}
                                            </Fragment>
                                        }

                                    </Table.Cell>
                                </Table.Row>
                                {!order.tableNumber ? null :
                                    <Table.Row>
                                        <Table.Cell>{i18next.t('tableNumber')}</Table.Cell>
                                        <Table.Cell>{order.tableNumber}</Table.Cell>
                                    </Table.Row>
                                }
                                {!order.posOrderId ? null :
                                    <Table.Row>
                                        <Table.Cell>{i18next.t('posId')}</Table.Cell>
                                        <Table.Cell>{order.posOrderId}</Table.Cell>
                                    </Table.Row>
                                }
                                <Table.Row>
                                    <Table.Cell>{i18next.t('source')}</Table.Cell>
                                    <Table.Cell>{order.orderType === 'kiosk' ? 'Kiosk' : 'App'}</Table.Cell>
                                </Table.Row>
                            </Table.Body>
                            {this.state.print ? null :
                                <Table.Footer fullWidth>
                                    <Table.Row>
                                        <Table.HeaderCell colSpan='4'>
                                            <Button
                                                floated="right"
                                                icon
                                                positive
                                                size='small'
                                                disabled={order.closeDate !== null}
                                                onClick={() => this.setState({ showFinishConfirmation: true })}
                                            >
                                                <Icon name='checkmark' /> {i18next.t('completeOrder')}
                                            </Button>
                                            {order.orderType === 'kiosk' ? null :
                                                <Button
                                                    floated="right"
                                                    icon
                                                    color='yellow'
                                                    size='small'
                                                    onClick={() => this.setState({ showCreditForm: true })}
                                                >
                                                    <Icon name='credit card' /> {i18next.t('creditOrder')}
                                                </Button>
                                            }
                                            <Button
                                                floated="right"
                                                icon
                                                negative
                                                size='small'
                                                disabled={order.orderType === 'kiosk' && !transactions.every(tran => ['tenbis', 'cibus', 'multipass', 'goodi'].includes(tran.paymentMethod))}
                                                onClick={() => this.setState({ showCancelationForm: true })}
                                            >
                                                <Icon name='cancel' /> {i18next.t('cancelOrder')}
                                            </Button>
                                            <Button
                                                floated="left"
                                                icon
                                                size='small'
                                                onClick={() => this.handlePrint()}
                                            >
                                                <Icon name='print' /> {i18next.t('print')}
                                            </Button>
                                            <Button
                                                floated="left"
                                                icon
                                                size='small'
                                                onClick={() => this.handleResendToPOS(restId, orderId)}
                                            >
                                                <Icon name='redo' /> {i18next.t('resendToPOS')}
                                            </Button>
                                            <Button
                                                floated="left"
                                                icon
                                                size='small'
                                                color="blue"
                                                onClick={() => this.handleGenerateReceipt(restId, orderId)}
                                            >
                                                <Icon name='file pdf' /> {i18next.t('generateReceipt')}

                                            </Button>
                                            {['fashion', 'attractions'].includes(this.props.restaurant.industry) &&
                                                <Button
                                                    floated="left"
                                                    icon
                                                    size='small'
                                                    color="blue"
                                                    onClick={() => this.handleGenerateReceipt(restId, orderId, true)}
                                                >
                                                    <Icon name='file pdf' /> {i18next.t('generateReplacementVoucher')}

                                                </Button>
                                            }
                                            <a id="downloadBtn" href="" download="receipt.pdf"></a>
                                        </Table.HeaderCell>
                                    </Table.Row>
                                </Table.Footer>
                            }
                        </Table>
                    </Grid.Column>
                    <Grid.Column>
                        <Table celled padded>
                            <Table.Header fullWidth>
                                <Table.Row>
                                    <Table.HeaderCell>{i18next.t('item')}</Table.HeaderCell>
                                    <Table.HeaderCell>{i18next.t('additions')}</Table.HeaderCell>
                                    <Table.HeaderCell>{i18next.t('comments')}</Table.HeaderCell>
                                    <Table.HeaderCell>{i18next.t('price')}</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {order.items.map((item) => {
                                    return (
                                        <Table.Row key={item.id}>
                                            <Table.Cell>{item.name}</Table.Cell>
                                            <Table.Cell>{this.renderItemDetails(item)}</Table.Cell>
                                            <Table.Cell>
                                                {item.remarks && <div>{item.remarks}</div>}
                                                {item.belongTo && <div><b>{`${i18next.t('name')}: `}</b>{item.belongTo}</div>}
                                            </Table.Cell>
                                            <Table.Cell>{formatPrice(item.price, this.props.restaurant.currencyCode, 2)}</Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>

                            <Table.Footer fullWidth >
                                <Table.Row>
                                    <Table.HeaderCell colSpan='4'>
                                        {(order.items || []).length + ' ' + i18next.t('items')}
                                        <Header size="small" floated='right'>
                                            {i18next.t('total')}: {formatPrice(order.totalPrice, this.props.restaurant.currencyCode, 2)}
                                        </Header>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </Grid.Column>
                </Grid>

                <CancelOrderModal
                    isVisible={this.state.showCancelationForm}
                    onClose={() => this.setState({ showCancelationForm: false })}
                    onApprove={(form) => this.handleCancelOrder(order, form)}
                />

                <CloseOrderModal
                    isVisible={this.state.showFinishConfirmation}
                    onClose={() => this.setState({ showFinishConfirmation: false })}
                    onApprove={() => this.handleCompleteOrder(order)}
                />

                <CreditOrderModal
                    order={order}
                    restaurant={this.props.restaurant || {}}
                    isVisible={this.state.showCreditForm}
                    onClose={() => this.setState({ showCreditForm: false })}
                    onApprove={(credit) => this.handleCreditOrder(credit)}
                />
            </div>
        );
    }
}

OrderDetails.propTypes = {
    restaurant: PropTypes.object,
    match: PropTypes.object,
    permissions: PropTypes.object,
    order: PropTypes.object,
    cancelOrder: PropTypes.func,
    creditPayment: PropTypes.func,
    closeOrder: PropTypes.func,
    resendOrder: PropTypes.func,
    generateReceipt: PropTypes.func,
    fetchOrder: PropTypes.func,
    confirm: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
    const { permissions } = state.auth;
    const restaurant = getCurrentRestaurant(state);

    return { permissions, restaurant };
};

export default connect(mapStateToProps,
    { closeOrder, cancelOrder, creditPayment, resendOrder, generateReceipt, confirm, fetchOrder })(OrderDetails);
